import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { fetchMixes } from "@/lib/api";
import { Play, ExternalLink } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const soundcloudEmbed = (url) =>
  `https://w.soundcloud.com/player/?url=${encodeURIComponent(
    url
  )}&color=%23ff2a2a&auto_play=false&hide_related=true&show_comments=false&show_user=true&show_reposts=false&show_teaser=false&visual=false`;

export default function Mixes() {
  const [mixes, setMixes] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

  useEffect(() => {
    (async () => {
      try {
        const data = await fetchMixes();
        setMixes(data);
        if (data.length > 0) setActiveId(data[0].id);
      } catch (e) {
        // noop
      }
    })();
  }, []);

  const active = mixes.find((m) => m.id === activeId);

  return (
    <section
      id="mixes"
      ref={sectionRef}
      data-testid="mixes-section"
      className="relative py-24 sm:py-32 border-b border-white/10 overflow-hidden"
    >
      {/* Ambient parallax bg */}
      <motion.div
        style={{ y: bgY }}
        className="absolute inset-0 opacity-[0.08] bg-cover bg-center will-change-transform"
        aria-hidden
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1759338245039-906f7558e743?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtaW5pbWFsJTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzg0MTA4ODR8MA&ixlib=rb-4.1.0&q=85)",
          }}
        />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
        <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#FF2A2A]">
              [ 03 / Audio ]
            </span>
            <h2 className="mt-4 font-heading font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[0.9]">
              Recorded
              <br />
              Frequencies
            </h2>
          </div>
          <a
            data-testid="mixes-soundcloud-cta"
            href="https://soundcloud.com/disko_bhute"
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono text-xs tracking-[0.25em] uppercase border border-white/30 text-white px-6 py-3 inline-flex items-center gap-2 hover:border-[#FF2A2A] hover:text-[#FF2A2A] transition-colors self-start"
          >
            Full catalogue on SoundCloud
            <ExternalLink size={14} />
          </a>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Active Player w/ real SoundCloud embed */}
          <div
            data-testid="mixes-player"
            className="lg:col-span-7 bg-[#0A0A0A] border border-white/10 p-4 sm:p-6 flex flex-col gap-4"
          >
            {active ? (
              <>
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.25em] uppercase">
                  <span className="text-[#FF2A2A]">● Now Playing</span>
                  <span className="text-[#8A8A8A]">
                    {active.bpm} BPM · {active.genre}
                  </span>
                </div>

                <div className="w-full h-[166px] bg-black">
                  <iframe
                    key={active.id}
                    data-testid="soundcloud-iframe"
                    title={active.title}
                    width="100%"
                    height="166"
                    scrolling="no"
                    frameBorder="no"
                    allow="autoplay"
                    src={soundcloudEmbed(active.soundcloud_url)}
                    className="w-full h-full block"
                  />
                </div>

                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                  <div>
                    <h3 className="font-heading font-bold uppercase tracking-tight text-xl sm:text-2xl">
                      {active.title}
                    </h3>
                    <p className="font-mono text-xs tracking-[0.2em] uppercase text-[#8A8A8A] mt-2">
                      {active.duration} · Released {active.release_date}
                    </p>
                  </div>
                  <a
                    data-testid="player-soundcloud-link"
                    href={active.soundcloud_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="self-start font-mono text-[10px] tracking-[0.25em] uppercase text-[#F5F5F5]/70 hover:text-[#FF2A2A] inline-flex items-center gap-2 transition-colors"
                  >
                    Open in SoundCloud <ExternalLink size={12} />
                  </a>
                </div>
              </>
            ) : (
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#8A8A8A]">
                No mixes yet.
              </p>
            )}
          </div>

          {/* Mix List */}
          <ul data-testid="mixes-list" className="lg:col-span-5 flex flex-col">
            {mixes.map((m, i) => (
              <li key={m.id}>
                <button
                  data-testid={`mix-row-${m.id}`}
                  onClick={() => setActiveId(m.id)}
                  className={`group w-full text-left grid grid-cols-12 gap-3 items-center py-5 border-b border-white/10 hover:border-[#FF2A2A] transition-colors ${
                    activeId === m.id ? "bg-white/[0.03]" : ""
                  }`}
                >
                  <div className="col-span-2 font-mono text-xs text-[#8A8A8A]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-8">
                    <h4 className="font-heading font-bold uppercase tracking-tight text-base sm:text-lg group-hover:text-white transition-colors">
                      {m.title}
                    </h4>
                    <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8A8A8A] mt-1">
                      {m.genre} · {m.bpm} BPM · {m.duration}
                    </p>
                  </div>
                  <div className="col-span-2 flex justify-end">
                    <span
                      className={`w-9 h-9 flex items-center justify-center border ${
                        activeId === m.id
                          ? "bg-[#FF2A2A] text-black border-[#FF2A2A]"
                          : "border-white/20 text-[#F5F5F5] group-hover:border-[#FF2A2A] group-hover:text-[#FF2A2A]"
                      } transition-colors`}
                    >
                      <Play size={14} />
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}
